import { defineStore } from 'pinia'

export type McpChatStepKind =
  | 'user'
  | 'plan'
  | 'tool_call_start'
  | 'tool_call_result'
  | 'narration'
  | 'done'
  | 'error'

export type McpChatStep = {
  id: number
  kind: McpChatStepKind
  text: string
  tool?: string
  ok?: boolean
}

/** Areas of the live UI that can be visually flashed when data changes. */
export type FlashArea = 'users' | 'extensions' | 'devices'

type State = {
  isOpen: boolean
  isRunning: boolean
  isSyncing: boolean
  steps: McpChatStep[]
  flash: Record<FlashArea, boolean>
}

let stepCounter = 0

const getDefaultState = (): State => ({
  isOpen: false,
  isRunning: false,
  isSyncing: false,
  steps: [],
  flash: {
    users: false,
    extensions: false,
    devices: false,
  },
})

export const useMcpChatStore = defineStore('mcpChat', {
  state: getDefaultState,

  actions: {
    open() {
      this.isOpen = true
    },

    close() {
      this.isOpen = false
    },

    toggle() {
      this.isOpen = !this.isOpen
    },

    reset() {
      this.steps = []
    },

    start() {
      this.isRunning = true
    },

    finish() {
      this.isRunning = false
    },

    /** Mark the live stores as syncing so the UI can show a reload animation. */
    startSync() {
      this.isSyncing = true
    },

    stopSync() {
      this.isSyncing = false
    },

    addStep(step: Omit<McpChatStep, 'id'>) {
      stepCounter += 1
      this.steps.push({ id: stepCounter, ...step })
    },

    /** Briefly highlight an area of the live UI after its data changed. */
    triggerFlash(area: FlashArea) {
      this.flash[area] = true
      window.setTimeout(() => {
        this.flash[area] = false
      }, 2500)
    },
  },
})
